<script setup lang="ts">
  import { ref, onMounted, inject } from 'vue'
  import { FileText, ChevronRightIcon } from 'lucide-vue-next'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useToast } from '@/components/ui/toast'
  import { useRouter } from 'vue-router'
  import { DocumentsService } from '@/types/generated'

  const router = useRouter()
  const store = useCaseStore()
  const { toast } = useToast()
  const isLoading = ref(false)
  const displayedDocuments = ref([])

  // Expose action button for "Dokument generieren"
  const actionLabel = 'Dokument generieren'
  const actionIcon = FileText
  const handleAction = () => {
    router.push('/document-generation')
  }
  defineExpose({
    actionLabel,
    actionIcon,
    handleAction
  })

  onMounted(async () => {
    await loadDocuments()
  })

  const loadDocuments = async () => {
    try {
      isLoading.value = true

      // Get all documents from the case record
      const allDocuments = [...(store.caseRecord?.documents || [])]

      // Filter only "KI generiertes Dokument" document types
      const filteredDocuments = allDocuments.filter(
        (doc) => doc.document_type === 'KI generiertes Dokument'
      )

      // Sort documents by date (newest first)
      const processedDocuments = filteredDocuments.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )

      // Get documents for display
      displayedDocuments.value = processedDocuments
    } catch (error) {
      console.error('Error loading documents:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Dokumente konnten nicht geladen werden'
      })
    } finally {
      isLoading.value = false
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  // Navigate to full documents view
  const viewAllDocuments = () => {
    if (store.caseRecord?.uuid) {
      router.push(`/app/cases/${store.caseRecord.uuid}/documents`)
    }
  }

  const registerWidgetActionHandler = inject('registerWidgetActionHandler')
  onMounted(() => {
    if (registerWidgetActionHandler) {
      registerWidgetActionHandler('GENERATED_DOCUMENTS', () => {
        if (store.caseRecord?.uuid) {
          router.push(`/app/cases/${store.caseRecord.uuid}/document-generation`)
        }
      })
    }
  })

  // Function to open document in new tab
  const openDocument = async (document: any) => {
    try {
      const response = await DocumentsService.documentsRetrieve({ uuid: document.uuid })
      if (response.url) {
        window.open(response.url, '_blank')
      } else {
        toast({
          variant: 'destructive',
          title: 'Fehler',
          description: 'Dokument konnte nicht geöffnet werden'
        })
      }
    } catch (error) {
      console.error('Error opening document:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Dokument konnte nicht geöffnet werden'
      })
    }
  }
</script>

<template>
  <div class="documents-widget">
    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="flex justify-center py-6"
    >
      <div
        class="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-purple-600"
      ></div>
    </div>

    <div
      v-else-if="displayedDocuments.length === 0"
      class="flex flex-col items-center justify-center rounded-lg border border-gray-300 px-4 py-10 shadow-sm"
    >
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          width="40"
          height="40"
          rx="8"
          fill="#F3E8FF"
        />
        <path
          d="M19 20H11M24 14H11M24 26H11M27 18L23 22M23 18L27 22"
          stroke="#7E22CE"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <p class="text-md mt-5 text-center text-gray-500">Keine Rechtsbezogene Daten extrahiert</p>
    </div>

    <!-- Documents List -->
    <div
      v-else
      class="space-y-2"
    >
      <div
        v-for="document in displayedDocuments"
        :key="document.uuid"
        class="flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3"
      >
        <!-- Document name -->
        <div
          class="cursor-pointer font-medium text-gray-800 hover:text-purple-600 hover:underline"
          @click="openDocument(document)"
        >
          {{ document.filename.replace('.pdf', '') }}
        </div>

        <!-- Badge and date -->
        <div class="flex items-center gap-3">
          <div class="flex items-center rounded-lg bg-purple-100 px-2 py-1 text-purple-700">
            <span class="mr-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-sparkles"
              >
                <path
                  d="m12 3-1.9 5.8a2 2 0 0 1-1.287 1.288L3 12l5.8 1.9a2 2 0 0 1 1.288 1.287L12 21l1.9-5.8a2 2 0 0 1 1.287-1.288L21 12l-5.8-1.9a2 2 0 0 1-1.288-1.287Z"
                />
              </svg>
            </span>
            KI-generiert
          </div>
          <div class="text-sm text-gray-800">
            {{ formatDate(document.created_at) }}
          </div>
        </div>
      </div>
    </div>

    <!-- View All Link -->
    <div class="items-left mt-4 flex justify-end">
      <a
        href=""
        class="inline-flex items-center text-sm font-medium text-purple-600 underline hover:text-purple-800"
        @click.prevent="viewAllDocuments"
      >
        Alle Dokumente ansehen
        <ChevronRightIcon class="ml-1 h-4 w-4" />
      </a>
    </div>
  </div>
</template>

<style scoped>
  .documents-widget {
    width: 100%;
    box-sizing: border-box;
  }
</style>
